/**
 * Permission codes the Organisation Hierarchies screens declare an interest in.
 *
 * They are the dot-strings `server/internal/foundation/hierarchy.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) — grep `PermHierarchy` there for the constants this file
 * mirrors. They live beside the feature the way `web/src/modules/org/permissions.ts` does,
 * one file per module boundary, rather than in `@/lib/permissions`.
 *
 * FSD 1.2.9.10 is the reason there are four and not two: "Hierarchy definition and
 * publication require the Enterprise Data Owner role; **publication of a security-purpose
 * hierarchy additionally requires the Security Administrator**, because scopes derive from
 * it." The server encodes that sentence as a three-step ladder — arrange, publish,
 * publish-a-security-hierarchy — and the screens keep the same three steps rather than
 * collapsing them into one "manage" flag, because collapsing them would show the Publish
 * button to the person who built the draft, which is exactly the maker/checker split the
 * sentence exists to create.
 *
 * `validate`, `compare` and `resolve` are all gated server-side on **read**, even though
 * `validate` is a POST — it persists nothing at all (`nodes.go`'s own comment on
 * `ValidateHierarchyVersion`: "this method writes nothing … It is a read"). So an Internal
 * Audit actor holding only `read` can open every screen in this module, run a validation,
 * compare two versions and resolve any date, and change nothing.
 */

export const HIERARCHY_PERMISSIONS = {
  /**
   * Views hierarchies, versions, validation findings, comparisons and resolution.
   * Every route in this module is gated on this and nothing stronger.
   */
  read: 'foundation.hierarchy.read',

  /**
   * Defines a hierarchy, creates draft versions, arranges nodes, submits a draft for
   * approval, and rolls back (which creates a new draft — F-4.006, never a mutation).
   * Deliberately NOT sufficient for Publish; see below.
   */
  manage: 'foundation.hierarchy.manage',

  /**
   * Approver-only (FSD 1.2.9.4: "Publish | Approver-only."). The point of no return:
   * the version becomes immutable, the closure cache and derived scopes are regenerated,
   * and the prior version is superseded at the effective boundary.
   */
  publish: 'foundation.hierarchy.publish',

  /**
   * The Security Administrator's **additional** authority over a hierarchy whose purpose
   * is `security` (FSD 1.2.9.10). The server requires it *on top of* `publish`, not
   * instead of it — `publish.go` runs the two `rbac.Require` calls in that order — so the
   * screens require both too, never one in place of the other.
   */
  securityPublish: 'foundation.hierarchy.security.publish',
} as const

export type HierarchyPermission =
  (typeof HIERARCHY_PERMISSIONS)[keyof typeof HIERARCHY_PERMISSIONS]
